import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../hooks/useAuth';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const { login } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    const goOnline = () => setIsOffline(false);
    const goOffline = () => setIsOffline(true);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!navigator.onLine) {
      setError('Kein Internetzugriff erkannt. Bitte WLAN/Mobilfunkdaten oder den Flugmodus prüfen und erneut versuchen.');
      return;
    }
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data.role === 'superadmin') navigate('/admin');
      else navigate('/portal');
    } catch (err) {
      if (!err.response) {
        // Kein HTTP-Response erhalten = Server/Internet nicht erreichbar, keine Zugangsdaten-Prüfung möglich
        setError('Server aktuell nicht erreichbar. Bitte Internetverbindung pruefen und erneut versuchen.');
      } else {
        setError(err.response?.data?.detail || 'Anmeldung fehlgeschlagen.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-cyan-50">
      <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-sm">
        <div className="text-center mb-8">
          <img src="/logo-wide.png" alt="IncoMail" className="h-16 mx-auto mb-4 object-contain" />
          <p className="text-sm text-gray-500 mt-1">Bitte melde dich an</p>
        </div>

        {isOffline && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 flex items-start gap-2">
            <span>📶</span>
            <span>Kein Internetzugriff erkannt. IncoMail benötigt für die Anmeldung eine Verbindung zum Server — bitte WLAN/Mobilfunkdaten oder den Flugmodus prüfen.</span>
          </div>
        )}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="login-email" className="block text-sm font-medium text-gray-700 mb-1">E-Mail</label>
            <input
              id="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="name@firma.de"
            />
          </div>
          <div>
            <label htmlFor="login-password" className="block text-sm font-medium text-gray-700 mb-1">Passwort</label>
            <input
              id="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            type="submit"
            disabled={loading || isOffline}
            className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium rounded-lg transition-colors"
          >
            {loading ? 'Anmelden...' : isOffline ? 'Kein Internet' : 'Anmelden'}
          </button>
        </form>
      </div>
    </div>
  );
}
